import PLAY_SVG from "assets/images/play.svg";
import STOP_SVG from "assets/images/stop.svg";
import { word } from "shared/types/Report";
import { useState, useRef } from "react";
import {
    ContentShell, Panel, PanelHeader, PanelTitle, CountBadge, EmptyBadge,
    EmptyState, EmptyTitle, EmptyText, ViewerTable, ViewerHeader, ViewerBody, ViewerRow, ViewerCol,
    ViewerIndex, ViewerStrong, ViewerThumb, ViewerAudioBox, ViewerPlayBtn
} from "../homeworkUi";

interface WordTableProps {
    list: word[]
}

const WordTableViewer = ({ list }: WordTableProps) => {
    const audioRefs = useRef<{[key: string]: HTMLAudioElement | null}>({});
    const [isPlayingList, setIsPlayingList] = useState<{[key: string]: boolean}>({});

    const handlePlayPause = (index: number, type: 'word' | 'example') => {
        const key = `${type}-${index}`;
        const audioRef = audioRefs.current[key];
        if (audioRef) {
            if (isPlayingList[key]) {
                audioRef.pause();
            } else {
                audioRef.play();
            }
            setIsPlayingList(prev => ({
                ...prev,
                [key]: !prev[key]
            }));
        }
    };

    const renderAudioPreview = (item: word, index: number, type: 'word' | 'example') => {
        const audio = type === 'word' ? item.word_audio : item.example_audio;
        if (audio) {
            const audioUrl = typeof audio === "string" ? audio : "";
            const key = `${type}-${index}`;
            return (
                <ViewerAudioBox>
                    <audio ref={(el) => (audioRefs.current[key] = el)} src={audioUrl} />
                    <ViewerPlayBtn
                        src={isPlayingList[key] ? STOP_SVG : PLAY_SVG}
                        alt="play"
                        onClick={() => handlePlayPause(index, type)}
                    />
                </ViewerAudioBox>
            );
        }
        return <EmptyBadge>없음</EmptyBadge>;
    };

    const renderImagePreview = (item: word) => {
        if (item.image_preview) {
            return <ViewerThumb src={item.image_preview} alt="preview" />
        } else if (item.image) {
            const imageUrl = typeof item.image === 'string' ? item.image : "";
            if (!imageUrl) return <EmptyBadge>없음</EmptyBadge>;
            return <ViewerThumb src={imageUrl} alt="preview" />
        }
        return <EmptyBadge>없음</EmptyBadge>;
    }

    if(!list.length){
        return (
            <ContentShell>
                <Panel>
                    <EmptyState>
                        <EmptyTitle>단어 콘텐츠가 없습니다</EmptyTitle>
                        <EmptyText>이 과제에 등록된 단어가 없거나 조회 권한이 없습니다.</EmptyText>
                    </EmptyState>
                </Panel>
            </ContentShell>
        )
    }

    return (
        <ContentShell>
            <Panel>
                <PanelHeader>
                    <PanelTitle>단어</PanelTitle>
                    <CountBadge>{list.length}개</CountBadge>
                </PanelHeader>
                <ViewerTable>
                    <ViewerHeader>
                        <ViewerCol style={{ width: "5%" }}>번호</ViewerCol>
                        <ViewerCol style={{ width: "16%" }}>단어</ViewerCol>
                        <ViewerCol style={{ width: "8%" }}>단어 음원</ViewerCol>
                        <ViewerCol style={{ width: "14%" }}>단어 뜻</ViewerCol>
                        <ViewerCol style={{ width: "22%" }}>예문</ViewerCol>
                        <ViewerCol style={{ width: "8%" }}>예문 음원</ViewerCol>
                        <ViewerCol style={{ width: "17%" }}>예문 해석</ViewerCol>
                        <ViewerCol style={{ width: "10%" }}>이미지</ViewerCol>
                    </ViewerHeader>
                    <ViewerBody>
                        {list.map((item, index) => (
                            <ViewerRow key={index + 1}>
                                <ViewerCol style={{ width: "5%" }}><ViewerIndex>{index + 1}</ViewerIndex></ViewerCol>
                                <ViewerCol style={{ width: "16%" }}><ViewerStrong>{item.word}</ViewerStrong></ViewerCol>
                                <ViewerCol style={{ width: "8%" }}>{renderAudioPreview(item, index, 'word')}</ViewerCol>
                                <ViewerCol style={{ width: "14%" }}>{item.kor}</ViewerCol>
                                <ViewerCol style={{ width: "22%" }}>{item.example || <EmptyBadge>없음</EmptyBadge>}</ViewerCol>
                                <ViewerCol style={{ width: "8%" }}>{renderAudioPreview(item, index, 'example')}</ViewerCol>
                                <ViewerCol style={{ width: "17%" }}>{item.example_kor || <EmptyBadge>없음</EmptyBadge>}</ViewerCol>
                                <ViewerCol style={{ width: "10%" }}>{renderImagePreview(item)}</ViewerCol>
                            </ViewerRow>
                        ))}
                    </ViewerBody>
                </ViewerTable>
            </Panel>
        </ContentShell>
    )
}

export default WordTableViewer;
